import { NumberInput } from '@mantine/core';
import { toastNotifications } from '@uag/react-core';
import { type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import {
    toDisplayValue,
    toInternalValue
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { type SelectedControl } from 'app/modules/wallforming/components/scenes/pivot-controls/context';
import { TransformationUnit } from 'app/modules/wallforming/components/scenes/pivot-controls/TransformationUnit';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

import 'app/modules/wallforming/custom-styles/customAnnotationStyle.css';

interface TransformationLabelProps {
    selectedControl: SelectedControl | null;
    snappingRotation: RefObject<number>;
    prevRadians: RefObject<number>;
    positionUpdated: RefObject<boolean>;
}

export const TransformationLabel = ({
    selectedControl,
    snappingRotation,
    prevRadians,
    positionUpdated
}: TransformationLabelProps) => {
    const { t } = useTranslation();

    const { unit, transformationLabel, setTransformationLabel } = usePlacementStore(
        useShallow((state) => ({
            unit: state.lengthUnit,
            transformationLabel: state.transformationLabel,
            setTransformationLabel: state.setTransformationLabel
        }))
    );

    const {
        pivotControlsRef,
        applyTranslationValue,
        applyRotationValue,
        handleOnArticleTransformationStart,
        handleOnArticleTransformationEnd,
        updatePivotControls
    } = usePlacementStore(
        useShallow((state) => ({
            pivotControlsRef: state.pivotControlsRef,
            applyTranslationValue: state.applyTranslationValue,
            applyRotationValue: state.applyRotationValue,
            handleOnArticleTransformationStart: state.handleOnArticleTransformationStart,
            handleOnArticleTransformationEnd: state.handleOnArticleTransformationEnd,
            updatePivotControls: state.updatePivotControls
        }))
    );

    const isTranslation = useMemo(() => selectedControl?.component === 'Arrow', [selectedControl]);
    const modeKey = useMemo(() => (isTranslation ? 'translation' : 'rotation'), [isTranslation]);

    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleOnApplyInput = useCallback(() => {
        if (!selectedControl || Number.isNaN(transformationLabel)) {
            return;
        }

        const isTranslation = selectedControl.component === 'Arrow';
        if (!isTranslation) {
            if (transformationLabel > 360 || transformationLabel < -360) {
                toastNotifications.information({ title: t('info'), message: t('infoDegreeInputValidation') });
                return;
            }
        }

        handleOnArticleTransformationStart(prevRadians, positionUpdated);

        if (isTranslation) {
            applyTranslationValue(selectedControl.axis, transformationLabel);
        }

        if (!isTranslation) {
            applyRotationValue(selectedControl.axis, transformationLabel, snappingRotation);
        }

        handleOnArticleTransformationEnd(false);
    }, [
        applyRotationValue,
        applyTranslationValue,
        handleOnArticleTransformationEnd,
        handleOnArticleTransformationStart,
        positionUpdated,
        prevRadians,
        selectedControl,
        snappingRotation,
        t,
        transformationLabel
    ]);

    const handleOnKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.code === 'KeyT') {
                updatePivotControls();
            }
        },
        [updatePivotControls]
    );

    const handleSetFocusOnInput = useCallback(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Enter' || !pivotControlsRef?.current) {
                return;
            }

            handleOnApplyInput();
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [handleOnApplyInput, pivotControlsRef]);

    useEffect(() => {
        if (!selectedControl) {
            return;
        }

        const timeoutId = setTimeout(() => {
            handleSetFocusOnInput();
        }, 5);

        return () => clearTimeout(timeoutId);
    }, [handleSetFocusOnInput, selectedControl]);

    if (!selectedControl) {
        return null;
    }

    return (
        <NumberInput
            key={modeKey}
            ref={inputRef}
            rightSection={<TransformationUnit isTranslation={isTranslation} />}
            step={0.01}
            style={{
                border: '1px solid #004588',
                height: '32px',
                fontSize: 10
            }}
            styles={{
                input: {
                    height: '31px',
                    color: PRIMARY_COLOR_ACCENT_HEX,
                    width: 80,
                    fontSize: 10,
                    border: 'none',
                    borderRadius: 0,
                    borderWidth: 0,
                    textAlign: 'right'
                }
            }}
            value={isTranslation ? toDisplayValue(transformationLabel, unit) : transformationLabel}
            hideControls
            onBlur={() => setTimeout(() => handleSetFocusOnInput(), 5)}
            onChange={(value) => {
                if (typeof value === 'number') {
                    setTransformationLabel(isTranslation ? toInternalValue(value, unit) : value);
                }
            }}
            onKeyDown={(e) => handleOnKeyDown(e)}
        />
    );
};
